import React from 'react';

import { useQuery } from '@tanstack/react-query';

import { Chip } from '@mui/material';

import { getPayments } from '~/services/PaymentsService';

import {
  type SocieteBenefitOrder,
  type SocieteBenefitPayment,
  orderHasActiveLoyaltyUpgrade,
  paymentsIncludeSocieteBenefit,
  toLoyaltyTierThemeKey,
} from './societeBenefitRedemption';

export const SOCIETE_BENEFIT_REDEEMED_LABEL = 'Société benefit redeemed';

type Props = {
  order: SocieteBenefitOrder & {
    id_orders?: string;
    lux_loyalty_tier?: string | null;
    payments?: Array<SocieteBenefitPayment> | unknown;
  };
};

export function SocieteBenefitRedeemedChip({ order }: Props) {
  const inlinePayments = Array.isArray(order.payments) ? (order.payments as Array<SocieteBenefitPayment>) : undefined;
  const hasUpgrade = orderHasActiveLoyaltyUpgrade(order);
  const redeemedFromOrder = hasUpgrade || paymentsIncludeSocieteBenefit(inlinePayments);

  // Lounge passes and airport transfers are only recorded as benefit payments.
  // The purchases list does not include payments, so load them when the items
  // alone cannot confirm a redemption. Shares the mobile card's query cache.
  const { data: paymentsData } = useQuery({
    queryKey: ['order-payments', order.id_orders],
    queryFn: () => getPayments(order.id_orders),
    enabled: Boolean(order.id_orders) && !redeemedFromOrder && inlinePayments === undefined,
  });

  const redeemed = redeemedFromOrder || paymentsIncludeSocieteBenefit(paymentsData?.result);
  if (!redeemed) {
    return null;
  }

  const tier = toLoyaltyTierThemeKey(order.lux_loyalty_tier);

  return (
    <Chip
      size="small"
      label={SOCIETE_BENEFIT_REDEEMED_LABEL}
      sx={(theme) => {
        const tierPalette = tier ? theme.palette.loyaltyTiers[tier] : undefined;

        return {
          // Same fixed height as OrderStatusChip. The account tier pill can grow past this.
          height: 24,
          maxHeight: 24,
          backgroundColor: 'transparent',
          backgroundImage: tierPalette?.gradient ?? 'none',
          ...(tierPalette
            ? { color: tierPalette.contrastText }
            : { backgroundColor: theme.palette.grey[800], color: theme.palette.common.white }),
        };
      }}
    />
  );
}
